import React, { useState, useRef, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import VideoPlayer from './VideoPlayer';

interface ZoomableVideoPlayerProps {
  src: string;
  className?: string;
  onError?: () => void;
  showControls?: boolean;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

const ZoomableVideoPlayer: React.FC<ZoomableVideoPlayerProps> = ({
  src,
  className = '',
  onError,
  showControls = true,
}) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(prev => {
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta));
      // Reset position when zooming out to 1x
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const maxOffset = ((zoom - 1) / 2) * rect.width;
      const maxOffsetY = ((zoom - 1) / 2) * rect.height;

      const newX = Math.min(maxOffset, Math.max(-maxOffset, e.clientX - dragStart.x));
      const newY = Math.min(maxOffsetY, Math.max(-maxOffsetY, e.clientY - dragStart.y));

      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, zoom, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (zoom > 1) {
      // Reset zoom
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    } else {
      // Zoom to 2x
      setZoom(2);
    }
  }, [zoom]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(MIN_ZOOM, prev - ZOOM_STEP);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  return (
    <div className={`w-full h-full ${className}`}>
      <div className="relative w-full h-full overflow-hidden">
        {/* Video container with transform */}
        <div
          className="absolute inset-0 transition-transform duration-100"
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transformOrigin: 'center center',
          }}
        >
          <VideoPlayer
            src={src}
            className="w-full h-full"
            onError={onError}
          />
        </div>

        {/* Transparent overlay for capturing mouse events when zoomed */}
        {zoom > 1 && (
          <div
            ref={containerRef}
            className="absolute inset-0 z-20"
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              background: 'transparent',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={handleDoubleClick}
          />
        )}

        {/* Mouse wheel capture layer (always active) */}
        <div
          ref={zoom <= 1 ? containerRef : undefined}
          className="absolute inset-0 z-10"
          style={{
            cursor: zoom > 1 ? 'grab' : 'default',
            background: 'transparent',
            pointerEvents: zoom > 1 ? 'none' : 'auto',
          }}
          onWheel={handleWheel}
          onDoubleClick={handleDoubleClick}
        />
      </div>

      {/* Zoom controls */}
      {showControls && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 rounded-lg p-1 z-30">
          <button
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-1.5 rounded hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="text-white text-xs font-medium min-w-[40px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-1.5 rounded hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
          {zoom > 1 && (
            <button
              onClick={handleReset}
              className="p-1.5 rounded hover:bg-white/20 text-white transition-colors ml-1"
              title="Reset zoom"
            >
              <RotateCcw size={16} />
            </button>
          )}
        </div>
      )}

      {/* Zoom indicator overlay (when zoomed) */}
      {zoom > 1 && (
        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded z-30">
          {zoom.toFixed(1)}x • Drag to pan • Double-click to reset
        </div>
      )}
    </div>
  );
};

export default React.memo(ZoomableVideoPlayer);
